// Pure formatting helpers for tachograph (.DDD) views — no Vue, reusable across components.
// Times come from the backend as ISO strings carrying the file's local offset, so we use
// moment.parseZone to keep that offset (never shift to the browser timezone).
import moment from 'moment'

function formatIso(iso: string, pattern: string, fallback = '—'): string {
  return iso ? moment.parseZone(iso).format(pattern) : fallback
}

/** Minutes -> "H:MM" (e.g. 150 -> "2:30"). Used for durations. */
export function hoursMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes || 0))
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`
}

/** Minute-of-day -> "HH:MM" clock label (1440 -> "00:00"). Used for axis/window labels. */
export function clock(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24
  const m = Math.round(minutes % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Group digits into thousands with a thin space (odometer / km). */
export function groupThousands(value: any): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/** ISO timestamp -> "HH:MM" in the file's local offset. */
export function formatTime(iso: string): string {
  return formatIso(iso, 'HH:mm', '')
}

/** ISO timestamp -> "YYYY-MM-DD" in the file's local offset. */
export function formatDate(iso: string): string {
  return formatIso(iso, 'YYYY-MM-DD', '')
}

/** ISO timestamp -> "DD.MM.YY". */
export function formatDateShort(iso: string): string {
  return formatIso(iso, 'DD.MM.YY')
}

/** ISO timestamp -> "DD.MM.YYYY", optionally with "HH:mm". */
export function formatDateFull(iso: string, includeTime = false): string {
  return formatIso(iso, includeTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY')
}

/** ISO timestamp -> "DD.MM HH:MM". */
export function formatDateTime(iso: string): string {
  return formatIso(iso, 'DD.MM HH:mm')
}

/** Minutes between two ISO timestamps, formatted "H:MM" (null/invalid -> "—"). */
export function durationBetween(beginIso: string | null, endIso: string | null): string {
  if (!beginIso || !endIso) return '—'
  const a = moment.parseZone(beginIso)
  const b = moment.parseZone(endIso)
  if (!a.isValid() || !b.isValid()) return '—'
  return hoursMinutes(Math.max(0, b.diff(a, 'minutes')))
}

/** Minute of the local day (0–1440) for an ISO timestamp (keeps the original offset). */
export function localMinuteOfDay(iso: string): number {
  const m = moment.parseZone(iso)
  return m.isValid() ? m.hours() * 60 + m.minutes() : 0
}

/** A far-future year is the tachograph "still in use" sentinel (open session/record). */
export function isOpenSentinelDate(iso: string): boolean {
  return iso ? parseInt(String(iso).slice(0, 4), 10) > 2090 : false
}
